<script setup>
import {computed, onMounted, provide, reactive, ref, watch} from "vue";
import useForm from "@/composables/useForm";
import projectsTasksService from "@/services/projects-tasks.service";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import StartupsSelector from "@/components/ui/StartupsSelector.vue";
import constants from "@/constants/constants";
import tasksService from "@/services/tasks.service";
import DatePicker from "vue2-datepicker";
import {decodeDate, encodeDateForServer} from "@/helpers/date";
import UserSelect from "@/components/ui/UserSelect.vue";
import {useStore} from "vue2-helpers/vuex";
import ProjectTaskTeam from "@/components/tasks/viewProjectTask/ProjectTaskTeam.vue";
import ConfirmModal from "@/components/ui/ConfirmModal.vue";

const {projectId} = defineProps(['projectId']);
const emit = defineEmits(['added']);
const store = useStore();

const form = reactive({
    title: "",
    description: "",
    startupId: null,
    userId: null,
    reporterId: null,
    watchersIds: [],
    date: null,
    status: "open",
    priority: "medium",
})

const {resetForm, formFilled, updateDefault, errors, hasErrors, clearErrors} = useForm(form);
const showModal = ref(false);
const startup = ref(null);
const reporter = ref(null);
const date = ref(null);
const usersList = ref([]);
const editField = ref(null);
const canHide = ref(false);
const showConfirmModal = ref(false);

watch(startup, value => {
    form.startupId = value?.id || null;
});

watch(reporter, value => {
    form.reporterId = value?.id || null;
});

watch(date, value => {
    form.date = value ? encodeDateForServer(value) : null;
});

onMounted(() => {
    getUsersList();
});

function checkFormChanged(event) {
    if (formFilled.value && !canHide.value) {
        event.preventDefault();
        showConfirmModal.value = true;
    }
}

function closeConfirmed(option) {
    showConfirmModal.value = false;

    if (option === 'discard') {
        canHide.value = true;
        showModal.value = false;
    }
}

async function getUsersList() {
    const {data} = await tasksService.getUsersList();
    usersList.value = data;
}

function addWatcher(userId) {
    form.watchersIds.push(userId);
}

function removeWatcher(userId) {
    form.watchersIds = form.watchersIds.filter(id => id !== userId);
}

async function save() {
    clearErrors();
    const {data} = await projectsTasksService.save(projectId, form);

    if (!hasErrors(data)) {
        canHide.value = true;
        showModal.value = false;
        emit('added');
    }
}

function open(status = null) {
    resetForm();
    startup.value = null;
    date.value = null;
    canHide.value = false;

    reporter.value = usersList.value.find(item => item.id === store.state.user.id);
    form.reporterId = reporter.value.id;

    if (status) {
        form.status = Object.keys(constants.tasksStatus)[status];
    }

    updateDefault();

    showModal.value = true;
}

provide('editField', editField);
provide('errorsUserId', computed(() => errors.userId));

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" title="Create A Task" modal-class="modal-w-xxl modal-access" @hide="checkFormChanged">
        <div class="access-content">
            <div class="access-content__main">
                <div class="form">
                    <div class="form__item w-75 mb-4 pb-2">
                        <h6 class="heading5 heading5--secondary mb-3">Task Title*</h6>
                        <span v-if="errors.title" class="form__error">{{errors.title[0]}}</span>
                        <b-form-input class="form-control--mid" v-model="form.title" placeholder="Add a title"></b-form-input>
                    </div>

                    <div class="row">
                        <div class="col-12 col-sm-3">
                            <div class="form__item mb-4 pb-2">
                                <h6 class="heading5 heading5--secondary mb-3">Task Status</h6>
                                <b-form-select class="selectbox selectbox-mid w-auto pr-5" v-model="form.status">
                                    <option
                                        v-for="(name, statusId) in constants.tasksStatus"
                                        :key="statusId"
                                        :value="statusId"
                                    >
                                        {{name}}
                                    </option>
                                </b-form-select>
                            </div>
                        </div>
                        <div class="col-12 col-sm-3">
                            <div class="form__item mb-4 pb-2">
                                <h6 class="heading5 heading5--secondary mb-3">Task Priority</h6>
                                <b-form-select class="selectbox selectbox-mid w-auto pr-5" v-model="form.priority">
                                    <option
                                        v-for="(name, priorityId) in constants.tasksPriorities"
                                        :key="priorityId"
                                        :value="priorityId"
                                    >
                                        {{name}}
                                    </option>
                                </b-form-select>
                            </div>
                        </div>
                    </div>

                    <div class="form__item mb-4 pb-2">
                        <h6 class="heading5 heading5--secondary mb-3">Task Description</h6>
                        <b-form-textarea class="form-control--mid form-control--midheight" v-model="form.description" placeholder="Add a description"/>
                    </div>

                    <dl class="definition-list definition-list--secondary">
                        <dt>Company</dt>
                        <dd>
                            <StartupsSelector
                                v-if="editField === 'startup'"
                                v-click-outside="() => editField = null"
                                v-model="startup"
                                @changed="editField = null"
                            />

                            <a class="link" v-else @click.stop="editField = 'startup'">
                                <template v-if="startup">{{startup.name}}</template>
                                <template v-else>Select A Startup</template>
                            </a>
                        </dd>

                        <dt>Reporter</dt>
                        <dd>
                            <div v-if="errors.reporterId" class="form__error">{{errors.reporterId[0]}}</div>
                            <UserSelect
                                v-if="editField === 'reporter'"
                                v-click-outside="() => editField = null"
                                v-model="reporter"
                                :options="usersList"
                                class="multiselect-mid mb-3"
                                @changed="editField = null"
                            />

                            <span v-else @click.stop="editField = 'reporter'">
                                <template v-if="reporter">{{reporter.name}}</template>

                                <a v-else class="link">Select Reporter</a>
                            </span>
                        </dd>

                        <dt>Due Date</dt>
                        <dd>
                            <DatePicker
                                v-if="editField === 'due_date'"
                                v-click-outside="() => editField = null"
                                v-model="date"
                                placeholder="Select a date"
                                format = "MM/DD/YYYY"
                                class="mb-2 w-100"
                                :append-to-body="false"
                            />

                            <div v-else class="editable" @click.stop="editField = 'due_date'">
                                {{ date ? decodeDate(date) : 'No date' }}
                            </div>
                        </dd>
                    </dl>
                </div>
            </div>

            <aside class="access-content__side">
                <ProjectTaskTeam
                    :user-id="form.userId"
                    :project-id="projectId"
                    @updateUserId="(id) => form.userId = id"
                    @addWatcher="addWatcher"
                    @removeWatcher="removeWatcher"
                />
            </aside>
        </div>

        <template #modal-footer>
            <PrimaryButton
                caption="Create Task"
                @click="save"
            />
        </template>

        <ConfirmModal
            :show.sync="showConfirmModal"
            title="Discard Task?"
            body="Your changes will not be saved"
            :options="[
                {label: 'Cancel', value: 'cancel'},
                {label: 'Discard', value: 'discard'}
            ]"
            @clicked="closeConfirmed"
        />
    </b-modal>
</template>

<style scoped>

</style>
